import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { FaArrowLeft, FaDownload } from 'react-icons/fa';

import { apiService } from '../../components/Config/api.service';
import { signalerErreur } from '../../components/Utils/signalerErreur';
import LoadingBarWithStyles from '../../components/Utils/LoadingBarWithStyles';
import { BOUTON_PRINCIPAL, BOUTON_SECONDAIRE, enveloppeDe } from '../Employee/organigrammeOutils';
import { CARTE, CLASSE_CHAMP, EtatDepart } from './departOutils';
import DepartChecklist from './DepartChecklist';
import DepartDomiciliations from './DepartDomiciliations';
import DepartStc from './DepartStc';

/**
 * La fiche d'un départ (contrats C34 et C35, Yros/Kavero#62) : ses faits, les
 * actions que son état permet, la checklist, le solde de tout compte calculé
 * par la Paie (avec les prêts en cours), les domiciliations du salarié et les
 * documents.
 *
 * Les actions suivent la machine à états d'Odoo : en brouillon, modifier,
 * lancer ou annuler ; lancé, valider le STC ou annuler ; STC validé, annuler.
 * Odoo reste juge (409, message affiché mot pour mot) ; la fiche ne change
 * qu'après sa réponse. Valider et annuler demandent une confirmation : l'un
 * clôt le contrat dans la Paie, l'autre le rouvre.
 */
const ETATS_DOCUMENT = ['lance', 'stc_valide', 'clos'];
const ETATS_RECU = ['stc_valide', 'clos'];
const ACTIONS = {
  brouillon: ['lancer', 'annuler'],
  lance: ['valider', 'annuler'],
  stc_valide: ['annuler'],
  clos: [],
  annule: [],
};
const A_CONFIRMER = ['valider', 'annuler'];

function FicheDepart() {
  const { t } = useTranslation();
  const { departId } = useParams();

  const [depart, setDepart] = useState(null);
  const [stc, setStc] = useState(null);
  const [erreurStc, setErreurStc] = useState('');
  const [motifs, setMotifs] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [message, setMessage] = useState('');
  const [action, setAction] = useState(null);
  const [edition, setEdition] = useState(null);

  const chargerStc = useCallback(async (courant) => {
    if (!ETATS_DOCUMENT.includes(courant.etat)) {
      setStc(null);
      setErreurStc('');
      return;
    }
    const reponse = await enveloppeDe(() => apiService.lireStc(courant.id));
    setStc(reponse.ok ? reponse.stc : null);
    setErreurStc(reponse.ok ? '' : reponse.message || 'generique');
  }, []);

  const appliquer = useCallback((nouveau) => {
    setDepart(nouveau);
    chargerStc(nouveau);
  }, [chargerStc]);

  useEffect(() => {
    let actif = true;
    (async () => {
      setChargement(true);
      const [reponse, listeMotifs] = await Promise.all([
        enveloppeDe(() => apiService.lireDepart(departId)),
        enveloppeDe(() => apiService.motifsDepart()),
      ]);
      if (!actif) return;
      if (reponse.ok) {
        appliquer(reponse.depart);
      } else {
        setErreur(reponse.message || 'generique');
      }
      setMotifs(listeMotifs.ok ? listeMotifs.motifs || [] : []);
      setChargement(false);
    })();
    return () => { actif = false; };
  }, [departId, appliquer]);

  const faireAvancer = async (cible) => {
    if (A_CONFIRMER.includes(cible) && !window.confirm(t(`departs.confirmer.${cible}`))) return;
    setAction(cible);
    setMessage('');
    const reponse = await enveloppeDe(() => apiService.faireAvancerDepart(depart.id, cible));
    setAction(null);
    if (reponse.ok) {
      appliquer(reponse.depart);
    } else {
      setMessage(reponse.message || t('departs.erreurGenerique'));
    }
  };

  const enregistrer = async (event) => {
    event.preventDefault();
    const valeurs = {
      motif_id: Number(edition.motifId),
      date_rupture: edition.dateRupture,
      jours_preavis: Number(edition.joursPreavis || 0),
    };
    if (edition.dateEntree) valeurs.date_entree = edition.dateEntree;
    setAction('modifier');
    setMessage('');
    const reponse = await enveloppeDe(() => apiService.modifierDepart(depart.id, valeurs));
    setAction(null);
    if (reponse.ok) {
      setEdition(null);
      appliquer(reponse.depart);
    } else {
      setMessage(reponse.message || t('departs.erreurGenerique'));
    }
  };

  const telecharger = async (document) => {
    try {
      await apiService.telechargerDocumentDepart(depart, document);
    } catch (e) {
      // Le corps d'erreur arrive en blob, illisible : le message reste local.
      signalerErreur(e, 'FicheDepart.telecharger');
      setMessage(t('departs.documents.erreur'));
    }
  };

  if (chargement) return <LoadingBarWithStyles />;
  if (erreur) {
    return (
      <div className="p-4">
        <p data-testid="depart-erreur" className="text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
          {erreur === 'generique' ? t('departs.erreurGenerique') : erreur}
        </p>
      </div>
    );
  }

  const commencerEdition = () => setEdition({
    motifId: String(depart.motif?.id || ''),
    dateRupture: depart.date_rupture,
    joursPreavis: String(depart.jours_preavis ?? 0),
    dateEntree: depart.date_entree || '',
  });

  return (
    <div className="p-4 space-y-4" data-testid="fiche-depart">
      <Link to="/rh/departs" className="inline-flex items-center gap-2 text-sm text-[--color-primary] hover:opacity-80">
        <FaArrowLeft size={12} /> {t('departs.retour')}
      </Link>

      <div className={CARTE}>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold text-gray-800">{depart.employe?.nom}</h1>
            <p className="text-sm text-gray-600">{depart.motif?.nom}</p>
          </div>
          <EtatDepart etat={depart.etat} />
        </div>

        {edition ? (
          <form data-testid="depart-edition" onSubmit={enregistrer} className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
            <label className="block text-sm font-medium">
              {t('departs.motif')}
              <select data-testid="depart-edition-motif" value={edition.motifId} className={CLASSE_CHAMP}
                onChange={(e) => setEdition({ ...edition, motifId: e.target.value })}>
                {motifs.map((motif) => <option key={motif.id} value={motif.id}>{motif.nom}</option>)}
              </select>
            </label>
            <label className="block text-sm font-medium">
              {t('departs.dateRupture')}
              <input type="date" data-testid="depart-edition-rupture" value={edition.dateRupture} className={CLASSE_CHAMP}
                onChange={(e) => setEdition({ ...edition, dateRupture: e.target.value })} />
            </label>
            <label className="block text-sm font-medium">
              {t('departs.joursPreavis')}
              <input type="number" min="0" data-testid="depart-edition-preavis" value={edition.joursPreavis}
                className={CLASSE_CHAMP} onChange={(e) => setEdition({ ...edition, joursPreavis: e.target.value })} />
            </label>
            <label className="block text-sm font-medium">
              {t('departs.dateEntree')}
              <input type="date" data-testid="depart-edition-entree" value={edition.dateEntree} className={CLASSE_CHAMP}
                onChange={(e) => setEdition({ ...edition, dateEntree: e.target.value })} />
            </label>
            <div className="md:col-span-2 flex justify-end gap-3">
              <button type="button" className={BOUTON_SECONDAIRE} onClick={() => setEdition(null)}>
                {t('departs.annuler')}
              </button>
              <button type="submit" data-testid="depart-enregistrer" disabled={action === 'modifier'} className={BOUTON_PRINCIPAL}>
                {t('departs.enregistrer')}
              </button>
            </div>
          </form>
        ) : (
          <dl className="mt-4 grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
            <div><dt className="text-gray-500">{t('departs.dateEntree')}</dt><dd data-testid="depart-date-entree">{depart.date_entree || '—'}</dd></div>
            <div><dt className="text-gray-500">{t('departs.dateRupture')}</dt><dd>{depart.date_rupture}</dd></div>
            <div><dt className="text-gray-500">{t('departs.joursPreavis')}</dt><dd>{depart.jours_preavis}</dd></div>
            <div>
              <dt className="text-gray-500">{t('departs.portail')}</dt>
              <dd data-testid="depart-portail">
                {depart.portail_coupe_le
                  ? t('departs.portailCoupe')
                  : t('departs.portailJusquau', { date: depart.date_coupure_portail })}
              </dd>
            </div>
          </dl>
        )}

        {message && (
          <p role="alert" data-testid="depart-message"
            className="mt-4 text-sm text-red-700 bg-red-50 border border-red-200 rounded-lg px-4 py-3">
            {message}
          </p>
        )}

        <div className="mt-4 flex flex-wrap gap-3">
          {depart.etat === 'brouillon' && !edition && (
            <button type="button" data-testid="depart-modifier" className={BOUTON_SECONDAIRE} onClick={commencerEdition}>
              {t('departs.modifier')}
            </button>
          )}
          {(ACTIONS[depart.etat] || []).map((cible) => (
            <button key={cible} type="button" data-testid={`depart-action-${cible}`}
              disabled={action !== null || edition !== null}
              onClick={() => faireAvancer(cible)}
              className={cible === 'annuler'
                ? 'px-4 py-2 text-sm rounded-lg text-red-600 hover:bg-red-50 disabled:opacity-50'
                : BOUTON_PRINCIPAL}>
              {t(`departs.actions.${cible}`)}
            </button>
          ))}
        </div>
      </div>

      {ETATS_DOCUMENT.includes(depart.etat) && (
        <DepartChecklist depart={depart} onDepart={setDepart} />
      )}

      {ETATS_DOCUMENT.includes(depart.etat) && (
        <DepartStc depart={depart} stc={stc} onStc={setStc}
          erreur={erreurStc === 'generique' ? t('departs.stc.erreur') : erreurStc} />
      )}

      <DepartDomiciliations depart={depart} onDepart={setDepart} />

      {ETATS_DOCUMENT.includes(depart.etat) && (
        <div className={CARTE}>
          <h2 className="text-base font-semibold mb-3">{t('departs.documents.titre')}</h2>
          <div className="flex flex-wrap gap-3">
            <button type="button" data-testid="depart-document-certificat" className={BOUTON_SECONDAIRE}
              onClick={() => telecharger('certificat')}>
              <span className="inline-flex items-center gap-2"><FaDownload size={12} /> {t('departs.documents.certificat')}</span>
            </button>
            {ETATS_RECU.includes(depart.etat) && stc && (
              <button type="button" data-testid="depart-document-recu" className={BOUTON_SECONDAIRE}
                onClick={() => telecharger('recu')}>
                <span className="inline-flex items-center gap-2"><FaDownload size={12} /> {t('departs.documents.recu')}</span>
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default FicheDepart;
